import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Input, Select, Tag, Tooltip, message, Button, Space, Typography, Popconfirm, Modal, Divider, DatePicker } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { SearchOutlined, CopyOutlined, ShopOutlined, QuestionCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  getShipments, searchCustomers, cancelShipment, getLogisticsProviders, getServices, getGateways,
} from '../../../services/logistics';
import type {
  Shipment, CustomerOption, ShipmentAddressSummary, LogisticsProvider, Service, Gateway,
} from '../../../services/logistics';
import { formatMoney, kgToLbOz, kgToKgGram, cmToInch } from '../shipping-rates/units';
import { useLabelPreview } from '../shared/useLabelPreview';
import { useAuth } from '../../../hooks/useAuth';
import TimezoneAwareTime from '../../../components/TimezoneAwareTime';
import ShipmentDetailModal from './ShipmentDetailModal';
import FeeAdjustmentModal from './FeeAdjustmentModal';
import type { ShipmentCopyPrefill } from '../shipping-rates/ParcelRateQuote';

const { Text } = Typography;
const { RangePicker } = DatePicker;

// 列头说明——统一用带圆环的标准问号图标（QuestionCircleOutlined），不用裸的 "?" 文本
const HeaderTip: React.FC<{ title: string; tip: string }> = ({ title, tip }) => (
  <Space size={4}>
    {title}
    <Tooltip title={tip}>
      <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
    </Tooltip>
  </Space>
);

const PAGE_SIZE = 10;

const RECORD_STATUS_COLORS: Record<string, string> = {
  active: 'green',
  voided: 'red',
};

// 同批次背景色——按批次在当前页第一次出现的顺序依次分配，不用哈希取模：只要当前页
// 同时出现的"多包裹批次"数量不超过这个调色板的颜色数，就不会有两个不同批次撞同一个颜色。
// 特意避开纯红/纯绿，防止和"作废/有效"状态标签的颜色语义混在一起引起误解。
const BATCH_ROW_COLORS = [
  '#e6f4ff', // 浅蓝
  '#f9f0ff', // 浅紫
  '#fff7e6', // 浅橙
  '#e6fffb', // 浅青
  '#fff0f6', // 浅粉
  '#f0f5ff', // 浅靛蓝
  '#fcffe6', // 浅黄绿
  '#fff2e8', // 浅橘红
];

const toNum = (v: string | null | undefined): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// 收发件地址两行展示：第一行姓名，第二行"城市 州省 邮编 国家"
const renderAddress = (addr: ShipmentAddressSummary | null) => {
  if (!addr) return <Text type="secondary">-</Text>;
  const line2 = [addr.city, addr.state, addr.postal_code, addr.country].filter(Boolean).join(' ');
  return (
    <div>
      <div>{addr.name || '-'}</div>
      <div><Text type="secondary" style={{ fontSize: 12 }}>{line2 || '-'}</Text></div>
    </div>
  );
};

// 包裹重量/尺寸两行展示：不确定购买时客户填的是公制还是英制（提交询价前前端就已经统一
// 换算成公制传给后端了，没留下"当时用的哪种单位"这个信息），所以两种单位都显示，不用去猜。
const renderPackageWeight = (record: Shipment) => {
  const weightKg = toNum(record.weight_kg);
  if (weightKg == null) {
    return <Text type="secondary">-</Text>;
  }
  const { lb, oz } = kgToLbOz(weightKg);
  const { kg, g } = kgToKgGram(weightKg);
  return (
    <div>
      <div>{lb || 0}磅{oz || 0}盎司</div>
      <div><Text type="secondary" style={{ fontSize: 12 }}>{kg || 0}公斤{g || 0}克</Text></div>
    </div>
  );
};

const renderPackageDimensions = (record: Shipment) => {
  const lengthCm = toNum(record.length_cm);
  const widthCm = toNum(record.width_cm);
  const heightCm = toNum(record.height_cm);
  if (lengthCm == null || widthCm == null || heightCm == null) {
    return <Text type="secondary">-</Text>;
  }
  const lengthIn = cmToInch(lengthCm);
  const widthIn = cmToInch(widthCm);
  const heightIn = cmToInch(heightCm);
  return (
    <div>
      <div>{lengthIn}*{widthIn}*{heightIn}英寸</div>
      <div><Text type="secondary" style={{ fontSize: 12 }}>{lengthCm}*{widthCm}*{heightCm}厘米</Text></div>
    </div>
  );
};

const ShipmentsListPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isStaffOrAdmin = user?.role === 'admin' || user?.role === 'staff';
  const isAdmin = user?.role === 'admin';
  // 物流报价页面对三种角色也是各自独立注册的 URL（和物流记录页面同一套约定），
  // 要按当前登录角色跳到对应的那一个。
  const shippingRatesPath = `/dashboard/${user?.role || 'customer'}/logistics/shipping-rates`;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Shipment[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [recordStatusFilter, setRecordStatusFilter] = useState<string | undefined>(undefined);
  const [depositReleasedFilter, setDepositReleasedFilter] = useState<'true' | 'false' | undefined>(undefined);
  const [customerFilter, setCustomerFilter] = useState<number | undefined>(undefined);
  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const customerSearchTimerRef = useRef<number | null>(null);
  const searchDebounceRef = useRef<number | null>(null);
  // 承运商/物流产品/网关筛选下拉的选项目录，页面进来时一次性拉全量，不用现搜现查
  const [providerOptions, setProviderOptions] = useState<LogisticsProvider[]>([]);
  const [serviceOptions, setServiceOptions] = useState<Service[]>([]);
  const [gatewayOptions, setGatewayOptions] = useState<Gateway[]>([]);
  const [providerFilter, setProviderFilter] = useState<number | undefined>(undefined);
  const [serviceFilter, setServiceFilter] = useState<number | undefined>(undefined);
  // 网关筛选仅管理员可见——员工/客户不需要关心具体走了哪个网关
  const [gatewayFilter, setGatewayFilter] = useState<number | undefined>(undefined);
  // 存纯字符串（YYYY-MM-DD），不存 Dayjs 对象——antd 内部打包的 dayjs 和项目自己装的 dayjs
  // 是两份不同的包，TS 会把两边的 Dayjs 类型当成不兼容的类型，用字符串规避这个问题，
  // 和项目里其它用 RangePicker 的页面（如 Bills.tsx）是同一个写法。
  const [dateRangeFilter, setDateRangeFilter] = useState<[string, string] | null>(null);
  const [viewingShipment, setViewingShipment] = useState<Shipment | null>(null);
  const [adjustingShipment, setAdjustingShipment] = useState<Shipment | null>(null);
  // 批量操作可以跨页选择，所以选中状态不能只存当前页的行——换页时不清空，只有搜索/筛选
  // 条件变了（结果集实质变了）才清空。selectedRows 配合 rowSelection 的
  // preserveSelectedRowKeys，跨页也能拿到完整的已选记录对象，不用自己再拼一份。
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<Shipment[]>([]);
  const [batchCancelling, setBatchCancelling] = useState(false);
  const [batchDownloading, setBatchDownloading] = useState(false);

  // 合并预览只对有效运单有意义——作废的运单标签不该被混进去一起打印，
  // 数量和实际合并的内容要对得上，不能显示"选中了几个"却只合并了一部分。
  const activeSelectedRows = useMemo(
    () => selectedRows.filter((r) => r.record_status === 'active'),
    [selectedRows],
  );

  const { openLabel, mergeAndPreview, merging, modal: labelPreviewModal } = useLabelPreview();

  // 按当前页数据里各批次第一次出现的顺序分配颜色；只在当前页内保证不重复，不追求全局唯一，
  // 每次拿到新的一页数据都重新计算，不用管排序或分页有没有把同批次的记录拆开。
  const batchColorMap = useMemo(() => {
    const map = new Map<string, string>();
    data.forEach((item) => {
      if (item.batch_size > 1 && item.purchase_batch_id && !map.has(item.purchase_batch_id)) {
        map.set(item.purchase_batch_id, BATCH_ROW_COLORS[map.size % BATCH_ROW_COLORS.length]);
      }
    });
    return map;
  }, [data]);

  // antd 固定列（fixed: 'left'/'right'）自带一层背景色，会盖住 onRow 设的行背景——
  // 固定列的单元格要用 onCell 单独再设一次同样的颜色，才能和其它列看起来一致。
  const getBatchRowColor = (record: Shipment): string | undefined =>
    (record.purchase_batch_id ? batchColorMap.get(record.purchase_batch_id) : undefined);
  const batchCellStyle = (record: Shipment) => {
    const color = getBatchRowColor(record);
    return color ? { style: { backgroundColor: color } } : {};
  };

  // 筛选条件一多，位置参数就快数不过来了——改成传一个覆盖对象，没传的字段沿用当前状态，
  // 每个筛选变化的地方只需要传自己关心的那一两个字段，不用把其它筛选条件的当前值也一起搬过来。
  interface FetchOverrides {
    page?: number;
    pageSize?: number;
    search?: string;
    recordStatus?: string;
    depositReleased?: 'true' | 'false';
    customer?: number;
    provider?: number;
    service?: number;
    gateway?: number;
    dateRange?: [string, string] | null;
  }

  const fetchData = async (overrides: FetchOverrides = {}) => {
    // 不能用 ?? 兜底当前 state——清空一个筛选框时，调用方传的就是 { customer: undefined }
    // 这种"显式清空"，如果用 ?? 会把 undefined 当成"没传"，又兜回清空前的旧值，导致列表
    // 看起来没刷新。用 in 判断这个 key 是否真的出现在 overrides 里，而不是看它的值是否为空。
    const p = 'page' in overrides ? overrides.page as number : page;
    const size = 'pageSize' in overrides ? overrides.pageSize as number : pageSize;
    const q = 'search' in overrides ? overrides.search : searchText;
    const recordStatus = 'recordStatus' in overrides ? overrides.recordStatus : recordStatusFilter;
    const depositReleased = 'depositReleased' in overrides ? overrides.depositReleased : depositReleasedFilter;
    const customer = 'customer' in overrides ? overrides.customer : customerFilter;
    const provider = 'provider' in overrides ? overrides.provider : providerFilter;
    const service = 'service' in overrides ? overrides.service : serviceFilter;
    const gateway = 'gateway' in overrides ? overrides.gateway : gatewayFilter;
    const dateRange = 'dateRange' in overrides ? overrides.dateRange : dateRangeFilter;
    setLoading(true);
    try {
      const resp = await getShipments({
        page: p,
        page_size: size,
        search: q || undefined,
        record_status: recordStatus || undefined,
        deposit_released: depositReleased || undefined,
        customer: isStaffOrAdmin ? customer : undefined,
        provider,
        service,
        gateway_account__gateway: isAdmin ? gateway : undefined,
        created_at__gte: dateRange ? dayjs(dateRange[0]).startOf('day').toISOString() : undefined,
        created_at__lte: dateRange ? dayjs(dateRange[1]).endOf('day').toISOString() : undefined,
      });
      const body = resp.data as any;
      setData(Array.isArray(body?.results) ? body.results : (Array.isArray(body) ? body : []));
      setTotal(Number(body?.count ?? 0) || 0);
      setPage(p);
      setPageSize(size);
    } catch {
      message.error('获取物流记录失败');
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const [providerResp, serviceResp] = await Promise.all([
        getLogisticsProviders({ page_size: 500 }),
        getServices({ page_size: 1000, is_active: true }),
      ]);
      const providerBody = providerResp.data as any;
      const serviceBody = serviceResp.data as any;
      setProviderOptions(Array.isArray(providerBody?.results) ? providerBody.results : (Array.isArray(providerBody) ? providerBody : []));
      setServiceOptions(Array.isArray(serviceBody?.results) ? serviceBody.results : (Array.isArray(serviceBody) ? serviceBody : []));
      if (isAdmin) {
        const gatewayResp = await getGateways({ page_size: 100 });
        const gatewayBody = gatewayResp.data as any;
        setGatewayOptions(Array.isArray(gatewayBody?.results) ? gatewayBody.results : (Array.isArray(gatewayBody) ? gatewayBody : []));
      }
    } catch (error) {
      console.error('获取承运商/物流产品/网关筛选选项失败:', error);
    }
  };

  useEffect(() => {
    fetchData({ page: 1 });
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    setSelectedRowKeys([]);
    setSelectedRows([]);
    if (searchDebounceRef.current) window.clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = window.setTimeout(() => {
      fetchData({ page: 1, search: value });
    }, 300);
  };

  const handleRecordStatusChange = (value: string | undefined) => {
    setRecordStatusFilter(value);
    setSelectedRowKeys([]);
    setSelectedRows([]);
    fetchData({ page: 1, recordStatus: value });
  };

  const handleDepositReleasedFilterChange = (value: 'true' | 'false' | undefined) => {
    setDepositReleasedFilter(value);
    setSelectedRowKeys([]);
    setSelectedRows([]);
    fetchData({ page: 1, depositReleased: value });
  };

  const handleCustomerFilterChange = (value: number | undefined) => {
    setCustomerFilter(value);
    setSelectedRowKeys([]);
    setSelectedRows([]);
    fetchData({ page: 1, customer: value });
  };

  const handleProviderFilterChange = (value: number | undefined) => {
    setProviderFilter(value);
    setSelectedRowKeys([]);
    setSelectedRows([]);
    fetchData({ page: 1, provider: value });
  };

  const handleServiceFilterChange = (value: number | undefined) => {
    setServiceFilter(value);
    setSelectedRowKeys([]);
    setSelectedRows([]);
    fetchData({ page: 1, service: value });
  };

  const handleGatewayFilterChange = (value: number | undefined) => {
    setGatewayFilter(value);
    setSelectedRowKeys([]);
    setSelectedRows([]);
    fetchData({ page: 1, gateway: value });
  };

  const handleDateRangeChange = (dates: any) => {
    const range: [string, string] | null = (dates && dates[0] && dates[1])
      ? [dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')]
      : null;
    setDateRangeFilter(range);
    setSelectedRowKeys([]);
    setSelectedRows([]);
    fetchData({ page: 1, dateRange: range });
  };

  const handleResetFilters = () => {
    setSearchText('');
    setRecordStatusFilter(undefined);
    setDepositReleasedFilter(undefined);
    setCustomerFilter(undefined);
    setProviderFilter(undefined);
    setServiceFilter(undefined);
    setGatewayFilter(undefined);
    setDateRangeFilter(null);
    setSelectedRowKeys([]);
    setSelectedRows([]);
    fetchData({
      page: 1,
      search: '',
      recordStatus: undefined,
      depositReleased: undefined,
      customer: undefined,
      provider: undefined,
      service: undefined,
      gateway: undefined,
      dateRange: null,
    });
  };

  const handleCustomerSearch = (keyword: string) => {
    if (customerSearchTimerRef.current) window.clearTimeout(customerSearchTimerRef.current);
    customerSearchTimerRef.current = window.setTimeout(async () => {
      setCustomerSearchLoading(true);
      try {
        setCustomerOptions(await searchCustomers(keyword));
      } catch (error) {
        console.error('查询客户列表失败:', error);
      } finally {
        setCustomerSearchLoading(false);
      }
    }, 300);
  };

  const handleCopyTrackingNumber = async (trackingNumber: string) => {
    try {
      await navigator.clipboard.writeText(trackingNumber);
      message.success('已复制');
    } catch {
      message.error('复制失败');
    }
  };

  // 复制运单：把这条已购买记录的发件人/收件人/包裹重量尺寸/签名保险电池信息整理成报价表单
  // 需要的结构，带到物流报价页面直接预填，不用重新手动填一遍。只复制这一个包裹本身的信息，
  // 不管它当时是不是多包裹批次购买的一部分——同批次其它包裹不会被一起复制过去。
  // 跟踪号/价格/网关等"购买结果"不属于可复制的输入信息，报价页面本来就会重新查一次全新的价格。
  const handleCopyShipment = (record: Shipment) => {
    const addressToPayload = (addr: ShipmentAddressSummary | null) => ({
      country: addr?.country || '',
      postal_code: addr?.postal_code || '',
      state: addr?.state || undefined,
      city: addr?.city || undefined,
      address_line: addr?.address_line || undefined,
      address_line2: addr?.address_line2 || undefined,
      name: addr?.name || undefined,
      company: addr?.company || undefined,
      phone: addr?.phone || undefined,
      email: addr?.email || undefined,
    });
    const weightKg = toNum(record.weight_kg);
    const { kg, g } = weightKg != null ? kgToKgGram(weightKg) : { kg: undefined, g: undefined };
    const lengthCm = toNum(record.length_cm);
    const widthCm = toNum(record.width_cm);
    const heightCm = toNum(record.height_cm);
    const insuranceAmount = record.insurance_amount != null ? Number(record.insurance_amount) : undefined;

    const prefill: ShipmentCopyPrefill = {
      origin: addressToPayload(record.sender),
      destination: addressToPayload(record.recipient),
      packages: [{
        weight_kg: kg,
        weight_g: g,
        length_cm: lengthCm != null ? Math.round(lengthCm) : undefined,
        width_cm: widthCm != null ? Math.round(widthCm) : undefined,
        height_cm: heightCm != null ? Math.round(heightCm) : undefined,
        quantity: 1,
      }],
      signature_required: record.signature_required,
      insurance_required: !!insuranceAmount,
      insurance_amount: insuranceAmount,
      battery_declared: record.battery_declared,
      battery_chemistry: (record.battery_chemistry || undefined) as ShipmentCopyPrefill['battery_chemistry'],
      battery_installation: (record.battery_installation || undefined) as ShipmentCopyPrefill['battery_installation'],
      remarks: record.remarks || '',
    };
    navigate(shippingRatesPath, { state: { shipmentPrefill: prefill } });
  };

  // 作废运单：后端会真正调用 Gateway 作废这张标签，承运商同意才会把状态改成作废；
  // 不涉及退款，保证金/运费需要联系客服另外走运费调整流程处理。
  const handleCancel = async (record: Shipment) => {
    try {
      await cancelShipment(record.id);
      message.success('已作废该运单');
      fetchData();
    } catch (error: any) {
      message.error(error?.response?.data?.detail || '作废运单失败');
    }
  };

  // 批量作废：先弹窗确认，再逐个调用作废接口——每个包裹各自独立成功/失败（承运商可能只
  // 拒绝其中一部分，比如已经揽收的），不是全有或全无，最后汇总一个结果提示。
  const executeBatchCancel = async (targets: Shipment[]) => {
    setBatchCancelling(true);
    try {
      const results = await Promise.allSettled(targets.map((s) => cancelShipment(s.id)));
      const successCount = results.filter((r) => r.status === 'fulfilled').length;
      const failCount = results.length - successCount;
      if (failCount === 0) {
        message.success(`已成功作废 ${successCount} 个运单`);
      } else {
        message.warning(`${successCount} 个作废成功，${failCount} 个失败（可能已进入运输环节，或其他原因），请查看列表确认`);
      }
      setSelectedRowKeys([]);
      setSelectedRows([]);
      fetchData();
    } finally {
      setBatchCancelling(false);
    }
  };

  const handleBatchCancelClick = () => {
    const activeSelected = selectedRows.filter((r) => r.record_status === 'active');
    const voidedCount = selectedRows.length - activeSelected.length;
    if (activeSelected.length === 0) {
      message.warning('选中的记录里没有可以作废的（都已经是作废状态）');
      return;
    }
    Modal.confirm({
      title: `确定要作废选中的 ${activeSelected.length} 个运单吗？`,
      content: (
        <>
          {voidedCount > 0 && (
            <div style={{ color: '#faad14', marginBottom: 8 }}>
              （有{voidedCount}条记录已经作废，无法再次执行作废操作）
            </div>
          )}
          <div>
            系统会通知承运商作废这些运单；如果某个包裹已经揽收进入运输环节，承运商可能会拒绝作废。
            运费将在承运商确认作废后由客服人工退回，大概需要30-60天；保证金不受影响，购买满 60 天后会照常自动解冻退回。
          </div>
        </>
      ),
      okText: '确定作废',
      okType: 'danger',
      cancelText: '再想想',
      onOk: () => executeBatchCancel(activeSelected),
    });
  };

  const handleBatchMergePreview = () => {
    if (activeSelectedRows.length === 0) return;
    mergeAndPreview(activeSelectedRows, '运单');
  };

  // 导出 Excel 用的行——字段顺序和含义跟列表列基本对应，员工/管理员额外带网关相关字段。
  const buildExportRow = (record: Shipment) => {
    const weightKg = toNum(record.weight_kg);
    const lengthCm = toNum(record.length_cm);
    const widthCm = toNum(record.width_cm);
    const heightCm = toNum(record.height_cm);
    const quoted = toNum(record.quoted_shipping_fee);
    const deposit = toNum(record.deposit_frozen);
    const final = toNum(record.final_shipping_fee);
    const cost = toNum(record.gateway_cost_price);
    // 和运单详情弹窗（ShipmentDetailModal）显示的字段保持一致——姓名/公司、电话、
    // 完整地址、城市州邮编、国家，只是从一个 Descriptions 表格拆成一组独立的列，
    // 方便在 Excel 里直接按字段筛选/排序，不用把所有信息挤在一个单元格里。
    const addressColumns = (addr: ShipmentAddressSummary | null, prefix: string) => ({
      [`${prefix}姓名/公司`]: addr ? `${addr.name || ''}${addr.company ? `（${addr.company}）` : ''}` : '',
      [`${prefix}电话`]: addr?.phone || '',
      [`${prefix}地址`]: addr ? `${addr.address_line || ''}${addr.address_line2 ? `，${addr.address_line2}` : ''}` : '',
      [`${prefix}城市州邮编`]: addr ? [addr.city, addr.state, addr.postal_code].filter(Boolean).join(' ') : '',
      [`${prefix}国家`]: addr?.country || '',
    });

    return {
      承运商: record.carrier_name,
      物流产品: record.service_name || '',
      跟踪号: record.tracking_number,
      ...(isStaffOrAdmin ? { 所属客户编码: record.customer_code || '', 所属客户: record.customer_name } : {}),
      ...addressColumns(record.sender, '发件人'),
      ...addressColumns(record.recipient, '收件人'),
      '重量(kg)': weightKg ?? '',
      '尺寸(cm)': (lengthCm != null && widthCm != null && heightCm != null) ? `${lengthCm}*${widthCm}*${heightCm}` : '',
      币种: record.currency,
      购买时运费: quoted ?? '',
      保证金: deposit ?? '',
      购买时支付总额: (quoted != null && deposit != null) ? quoted + deposit : '',
      运费调整: (quoted != null && final != null && quoted !== final) ? final - quoted : 0,
      最终运费: final ?? '',
      状态: record.record_status_display,
      ...(isStaffOrAdmin ? {
        网关: record.gateway_name || '',
        购买时网关成本价: cost ?? '',
        理论利润: (quoted != null && cost != null) ? quoted - cost : '',
      } : {}),
      购买时间: record.created_at ? new Date(record.created_at).toLocaleString('zh-CN') : '',
    };
  };

  const handleBatchDownload = async () => {
    if (selectedRows.length === 0) return;
    setBatchDownloading(true);
    try {
      const XLSX = await import('xlsx');
      const ws = XLSX.utils.json_to_sheet(selectedRows.map(buildExportRow));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '运单');
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const ts = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} `
        + `${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
      XLSX.writeFile(wb, `运单批量下载-${selectedRows.length}条-${ts}.xlsx`);
      message.success(`下载成功，共导出 ${selectedRows.length} 条记录`);
    } catch (error) {
      console.error('批量下载失败:', error);
      message.error('批量下载失败，请重试');
    } finally {
      setBatchDownloading(false);
    }
  };

  const columns: ColumnsType<Shipment> = [
    {
      title: '承运商/物流产品',
      key: 'carrier',
      width: 200,
      fixed: 'left',
      onCell: batchCellStyle,
      render: (_, record) => (
        <Space>
          <div style={{ width: 32, height: 32, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {record.carrier_logo ? (
              <img src={record.carrier_logo} alt={record.carrier_name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            ) : (
              <ShopOutlined style={{ color: '#bfbfbf', fontSize: 18 }} />
            )}
          </div>
          <div>
            <div>{record.carrier_name}</div>
            <div><Text type="secondary" style={{ fontSize: 12 }}>{record.service_name || '-'}</Text></div>
          </div>
        </Space>
      ),
    },
    {
      title: '跟踪号',
      key: 'tracking_number',
      width: 220,
      fixed: 'left',
      onCell: batchCellStyle,
      render: (_, record) => (
        <Space size={4} wrap>
          <Text>{record.tracking_number}</Text>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleCopyTrackingNumber(record.tracking_number)}
          />
          {record.batch_size > 1 && (
            <Tooltip title="这个跟踪号是同一次购买里其中一个包裹的，同一批一共产生了几个独立的跟踪号；同批次的记录在列表里用同样的背景色标出来了">
              <Tag color="blue">同批{record.batch_size}件</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    ...(isStaffOrAdmin ? [{
      title: '所属客户',
      key: 'customer',
      width: 150,
      render: (_: any, record: Shipment) => (
        <div>
          <div>{record.customer_code || '-'}</div>
          <div><Text type="secondary" style={{ fontSize: 12 }}>{record.customer_name}</Text></div>
        </div>
      ),
    } as const] : []),
    {
      title: '发件人',
      key: 'sender',
      width: 170,
      render: (_, record) => renderAddress(record.sender),
    },
    {
      title: '收件人',
      key: 'recipient',
      width: 170,
      render: (_, record) => renderAddress(record.recipient),
    },
    {
      title: '包裹重量',
      key: 'package_weight',
      width: 140,
      render: (_, record) => renderPackageWeight(record),
    },
    {
      title: '包裹尺寸',
      key: 'package_dimensions',
      width: 160,
      render: (_, record) => renderPackageDimensions(record),
    },
    {
      title: (
        <HeaderTip
          title="预估运费"
          tip="购买运单时的估算金额；承运商实际揽收/结算后可能会重新核算并进行调整，最终以「最终运费」列为准。"
        />
      ),
      key: 'quoted_shipping_fee',
      width: 110,
      render: (_, record) => formatMoney(record.quoted_shipping_fee, record.currency),
    },
    {
      title: (
        <HeaderTip
          title="保证金"
          tip="购买时按物流产品规则预先冻结的保证金，专款专用，不会被运费调整挪用；购买满 60 天后自动解冻并计入账户可用余额，跟这个运单期间是否发生过运费调整无关。"
        />
      ),
      key: 'deposit_frozen',
      width: 110,
      render: (_, record) => (
        <Space size={4}>
          <span>{formatMoney(record.deposit_frozen, record.currency)}</span>
          {record.deposit_released && <Tag color="default">已退</Tag>}
        </Space>
      ),
    },
    {
      title: (
        <HeaderTip
          title="购买时支付总额"
          tip="= 预估运费 + 保证金；其中保证金可退，购买满 60 天后自动退还，不计入实际物流成本。"
        />
      ),
      key: 'purchase_total',
      width: 150,
      render: (_, record) => {
        const quoted = toNum(record.quoted_shipping_fee) || 0;
        const deposit = toNum(record.deposit_frozen) || 0;
        return formatMoney(quoted + deposit, record.currency);
      },
    },
    {
      title: (
        <HeaderTip
          title="运费调整"
          tip="承运商实际揽收/结算后，可能因实际重量或尺寸与申报不符、地址类型（住宅/偏远地区）附加费、燃油附加费等原因重新核算运费，产生补收或退还的差额。调整通常发生在运单揽收后到承运商账单结算完成前（一般为发货后 30-90 天内，具体以承运商账单周期为准）。"
        />
      ),
      key: 'fee_adjustment',
      width: 110,
      render: (_, record) => {
        const quoted = toNum(record.quoted_shipping_fee);
        const final = toNum(record.final_shipping_fee);
        if (quoted == null || final == null || quoted === final) return <Text type="secondary">-</Text>;
        const diff = final - quoted;
        const isSurcharge = diff > 0;
        return (
          <Text style={{ color: isSurcharge ? '#f5222d' : '#52c41a' }}>
            {isSurcharge ? '+' : '-'}{Math.abs(diff).toFixed(2)}
          </Text>
        );
      },
    },
    {
      title: (
        <HeaderTip
          title="最终运费"
          tip="默认等于预估运费；只有承运商后续确实发生运费调整，这里才会显示调整后的最终金额，具体差额见「运费调整」列。"
        />
      ),
      key: 'final_shipping_fee',
      width: 110,
      render: (_, record) => {
        const quoted = toNum(record.quoted_shipping_fee);
        const final = toNum(record.final_shipping_fee);
        const text = formatMoney(record.final_shipping_fee, record.currency);
        if (quoted == null || final == null || quoted === final) return text;
        return <Text style={{ color: final > quoted ? '#f5222d' : '#52c41a' }}>{text}</Text>;
      },
    },
    {
      title: '状态',
      dataIndex: 'record_status',
      key: 'record_status',
      width: 90,
      render: (_, record) => (
        <Tag color={RECORD_STATUS_COLORS[record.record_status] || 'default'}>{record.record_status_display}</Tag>
      ),
    },
    ...(isStaffOrAdmin ? [
      {
        title: '网关',
        dataIndex: 'gateway_name',
        key: 'gateway_name',
        width: 100,
        render: (v: string | null) => v || '-',
      } as const,
      {
        title: '购买时网关成本价',
        key: 'gateway_cost_price',
        width: 140,
        render: (_: any, record: Shipment) => (
          record.gateway_cost_price == null ? '-' : formatMoney(record.gateway_cost_price, record.currency)
        ),
      } as const,
      {
        title: <HeaderTip title="理论利润" tip="购买时运费 - 购买时网关成本价" />,
        key: 'profit',
        width: 110,
        render: (_: any, record: Shipment) => {
          const quoted = toNum(record.quoted_shipping_fee);
          const cost = toNum(record.gateway_cost_price);
          if (quoted == null || cost == null) return '-';
          return formatMoney(quoted - cost, record.currency);
        },
      } as const,
    ] : []),
    {
      title: '购买时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (v: string) => (v ? <TimezoneAwareTime time={v} format="YYYY-MM-DD HH:mm:ss" showTimezone={false} /> : '-'),
    },
    {
      title: '操作',
      key: 'action',
      width: 250,
      fixed: 'right',
      onCell: batchCellStyle,
      render: (_, record) => (
        <Space size={4} wrap>
          <Button type="link" size="small" onClick={() => setViewingShipment(record)}>
            详情
          </Button>
          <Button
            type="link"
            size="small"
            disabled={!record.label_url || record.record_status === 'voided'}
            onClick={() => openLabel(record.label_url as string, record.tracking_number)}
          >
            打印运单
          </Button>
          <Button type="link" size="small" onClick={() => handleCopyShipment(record)}>
            复制运单
          </Button>
          <Popconfirm
            title={`确定要作废这个运单吗？${[record.service_name, record.tracking_number].filter(Boolean).join(' - ')}`}
            description="系统会通知承运商作废这张运单；如果包裹已经揽收进入运输环节，承运商可能会拒绝作废。运费将在承运商确认作废后由客服人工退回，大概需要30-60天；保证金不受影响，购买满 60 天后会照常自动解冻退回。"
            okText="确定作废"
            cancelText="再想想"
            disabled={record.record_status === 'voided'}
            onConfirm={() => handleCancel(record)}
          >
            <Button type="link" size="small" danger disabled={record.record_status === 'voided'}>作废运单</Button>
          </Popconfirm>
          {isAdmin && (
            <Button type="link" size="small" onClick={() => setAdjustingShipment(record)}>
              退还/调整运费
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>运单</h3>
          <Text type="secondary">已经通过物流引擎购买的所有运单{isStaffOrAdmin ? '（默认显示全部客户）' : ''}</Text>
        </div>
        <Space wrap>
          <Tooltip title="已经作废的运单无法再次作废">
            <Button danger disabled={activeSelectedRows.length === 0} loading={batchCancelling} onClick={handleBatchCancelClick}>
              批量作废({activeSelectedRows.length})
            </Button>
          </Tooltip>
          <Tooltip title="已经作废的运单无法预览">
            <Button disabled={activeSelectedRows.length === 0} loading={merging} onClick={handleBatchMergePreview}>
              合并运单预览({activeSelectedRows.length})
            </Button>
          </Tooltip>
          <Button disabled={selectedRows.length === 0} loading={batchDownloading} onClick={handleBatchDownload}>
            批量下载({selectedRows.length})
          </Button>
          <Divider type="vertical" style={{ margin: '0 4px', height: 24 }} />
          <Button type="primary" onClick={() => navigate(shippingRatesPath)}>
            创建新运单
          </Button>
        </Space>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <Input
          placeholder="搜索跟踪号"
          prefix={<SearchOutlined />}
          style={{ width: 220 }}
          value={searchText}
          onChange={(e) => handleSearchChange(e.target.value)}
          allowClear
        />
        <Select
          placeholder="运单状态"
          allowClear
          style={{ width: 160 }}
          value={recordStatusFilter}
          onChange={handleRecordStatusChange}
          options={[
            { value: 'active', label: '有效' },
            { value: 'voided', label: '作废' },
          ]}
        />
        <Select
          placeholder="保证金状态"
          allowClear
          style={{ width: 160 }}
          value={depositReleasedFilter}
          onChange={handleDepositReleasedFilterChange}
          options={[
            { value: 'false', label: '未解冻' },
            { value: 'true', label: '已解冻' },
          ]}
        />
        {isStaffOrAdmin && (
          <Select
            showSearch
            allowClear
            placeholder="按客户筛选（输入编码或名称搜索）"
            style={{ width: 260 }}
            value={customerFilter}
            onChange={handleCustomerFilterChange}
            onSearch={handleCustomerSearch}
            onDropdownVisibleChange={(open) => { if (open) handleCustomerSearch(''); }}
            filterOption={false}
            loading={customerSearchLoading}
            notFoundContent={customerSearchLoading ? '搜索中...' : '未找到匹配的客户'}
            options={customerOptions.map((c) => ({
              value: c.id,
              label: `${c.customer_code || '-'} ${c.company_name || c.username}`,
            }))}
          />
        )}
        <Select
          showSearch
          allowClear
          placeholder="按承运商筛选"
          style={{ width: 200 }}
          value={providerFilter}
          onChange={handleProviderFilterChange}
          filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          options={providerOptions.map((p) => ({ value: p.id, label: `${p.code}-${p.name}` }))}
        />
        <Select
          showSearch
          allowClear
          placeholder="按物流产品筛选"
          style={{ width: 280 }}
          value={serviceFilter}
          onChange={handleServiceFilterChange}
          filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          options={serviceOptions.map((s) => ({
            value: s.id,
            label: s.logistics_provider_name ? `${s.logistics_provider_name} - ${s.name}` : s.name,
          }))}
        />
        {isAdmin && (
          <Select
            showSearch
            allowClear
            placeholder="按网关筛选"
            style={{ width: 160 }}
            value={gatewayFilter}
            onChange={handleGatewayFilterChange}
            filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            options={gatewayOptions.map((g) => ({ value: g.id, label: g.name }))}
          />
        )}
        <RangePicker
          allowClear
          placeholder={['购买时间从', '购买时间至']}
          value={[
            dateRangeFilter?.[0] ? dayjs(dateRangeFilter[0]) : null,
            dateRangeFilter?.[1] ? dayjs(dateRangeFilter[1]) : null,
          ] as any}
          onChange={handleDateRangeChange}
        />
        <Button icon={<ReloadOutlined />} onClick={handleResetFilters}>
          重置
        </Button>
      </div>
      <Table<Shipment>
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        rowSelection={{
          selectedRowKeys,
          // 跨页选择需要这个：不然翻页后 antd 内部只认识当前页的行对象，
          // onChange 拿到的 selectedRows 就会把之前页面选中的记录丢掉。
          preserveSelectedRowKeys: true,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
          },
          fixed: 'left',
        }}
        onRow={(record) => {
          const color = getBatchRowColor(record);
          return color ? { style: { backgroundColor: color } } : {};
        }}
        scroll={{ x: isStaffOrAdmin ? 2480 : 1880 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (t, range) => `共 ${t} 条记录 第${range[0]}-${range[1]}条`,
          onChange: (p, ps) => fetchData({ page: p, pageSize: ps }),
        }}
        locale={{ emptyText: '暂无物流记录' }}
      />
      {labelPreviewModal}
      <ShipmentDetailModal shipment={viewingShipment} onClose={() => setViewingShipment(null)} />
      <FeeAdjustmentModal
        shipment={adjustingShipment}
        onClose={() => setAdjustingShipment(null)}
        onSuccess={() => fetchData()}
      />
    </Card>
  );
};

export default ShipmentsListPage;
